import type { TemplateBlockInstance } from '@/templates/domain/types';

/** The slice of the preview context the "does this block print" rule reads. */
type RenderableContext = {
  customer?: { tags?: readonly string[] } | undefined;
  totals?: { has_line_items?: boolean } | undefined;
};

const isTotalsInstance = (instance: { importedBlockJson?: unknown }): boolean => {
  const json = instance.importedBlockJson as { content?: { kind?: unknown } } | null | undefined;
  return json?.content?.kind === 'total';
};

/**
 * Whether a composition instance prints at all for this context. Shared by the HTML preview
 * (`DocumentRender`) and the React-PDF document so both drop the same blocks:
 *  - `visible === false` never prints;
 *  - `hideForCustomerTags` hides the block for a customer carrying at least one of the tags;
 *  - a Totals block is dropped when the host says the quote has no own line items
 *    (`totals.has_line_items === false`, LLAMS-721). An absent flag - older hosts, the block / template editor's
 *    sample and empty contexts - never hides it.
 */
export function isInstanceRenderable(
  instance: Pick<TemplateBlockInstance, 'visible' | 'hideForCustomerTags'> & { importedBlockJson?: unknown },
  context: RenderableContext
): boolean {
  if (instance.visible === false) return false;
  if (context.totals?.has_line_items === false && isTotalsInstance(instance)) return false;
  const hideTags = instance.hideForCustomerTags ?? [];
  if (hideTags.length === 0) return true;
  const customerTags = new Set(context.customer?.tags ?? []);
  return !hideTags.some((tag) => customerTags.has(tag));
}
